import moment from 'moment';
import { createContext, useContext, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import ExpiredSessionModal from '../components/ExpiredSessionModal';
import InactivityModal from '../components/InactivityModal';
import { ROLES, STORAGE } from '../constants/GlobalConstants';
import { redirectLogout, renewToken } from '../utils/RoutingUtils';
import { AppContext } from './AppContext';

const timeToInvalidate = () => {
  const token = STORAGE.get(STORAGE.TOKEN);

  const expMinusMinute = moment(token.expiration).subtract(1, 'minutes');
  const timeDiff = expMinusMinute.diff(moment());

  return moment.duration(timeDiff).valueOf();
};

const getRemainingMinutes = remainingMillis => {
  const oneMinute = 1000 * 60;
  const remaining = moment().add(remainingMillis + oneMinute, 'milliseconds');
  const timeDiff = remaining.diff(moment());

  const remainingMinutes = moment.duration(timeDiff).asMinutes();
  return remainingMinutes;
};

export const LoginContext = createContext();

const LoginContextProvider = ({ children }) => {
  const { showWarning } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [openExpiredSession, setOpenExpiredSession] = useState(false);

  const userInfo = STORAGE.get(STORAGE.USER_INFO) || redirectLogout();
  const idSupplier = userInfo.userName;
  const isSupplier = userInfo.supplier;
  const isAdmin = userInfo.role === ROLES.ADMIN;
  const isVisibleByRole = ![ROLES.AUDIT].includes(userInfo.role);
  const canSeeAllAreas = [ROLES.ADMIN, ROLES.AUDIT, ROLES.APAYABLE].includes(userInfo.role);
  const isAllowedByRole = () => {
    const rolesToBlock = [ROLES.AUDIT];

    if (rolesToBlock.includes(userInfo.role)) {
      showWarning('Acción no permitida para rol de usuario actual');
      return false;
    }

    return true;
  };

  const logoutByExpiration = () => {
    setOpen(false);
    setOpenExpiredSession(true);
    setTimeout(() => redirectLogout(), 2000);
  };

  const { getRemainingTime, getElapsedTime, reset } = useIdleTimer({
    timeout: timeToInvalidate(),
    onIdle: () => {
      setOpen(true);
    },
    onAction: () => {
      const remainingMinutes = getRemainingMinutes(getRemainingTime() - getElapsedTime());

      if (remainingMinutes <= 0) {
        logoutByExpiration();
      } else if (remainingMinutes <= 2) {
        setOpen(true);
      } else if (remainingMinutes < 3) {
        renewToken().then(res => reset());
      }
    },
    debounce: 500,
    crossTab: true,
  });

  return (
    <LoginContext.Provider
      value={{
        isSupplier,
        idSupplier,
        userInfo,
        isAdmin,
        isVisibleByRole,
        isAllowedByRole,
        canSeeAllAreas,
      }}>
      <InactivityModal open={open} setOpen={setOpen} reset={reset} />
      <ExpiredSessionModal open={openExpiredSession} />
      {children}
    </LoginContext.Provider>
  );
};

export default LoginContextProvider;
